////////////////////////////////
// Component for use in creating new icons only, not intended for use outside of the 'icon' director
////////////////////////////////

import React from 'react'
import { makeStyles } from 'tss-react/mui'
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon'

const useStyles = makeStyles()((theme) => ({
  warning: {
    fill: theme.palette.warning.main,
  },
  success: {
    fill: theme.palette.success.main,
  },
}))

export type ExtendedColorProps = SvgIconProps['color'] | 'success' | 'warning'

export interface BaseIconProps
  extends Omit<SvgIconProps, 'height' | 'width' | 'color'> {
  color?: ExtendedColorProps
  /**
   * @deprecated Use viebox to override the native viewbox.
   * Native viewbox comes from the raw SVG and ids translated into JSX markup.
   * If you need to override the native value just use MUI Icons viewbox string.
   */
  nativeViewbox?: string
}

interface IconProps extends Omit<BaseIconProps, 'width' | 'height'> {
  children: React.ReactNode
}

const Icon: React.FC<IconProps> = ({
  children,
  color,
  nativeViewbox,
  ...rest
}) => {
  const { classes, cx } = useStyles()

  const isMuiColor = color !== 'success' && color !== 'warning'

  // SVGs render best when they have a viewbox to know the canvas space to paint.
  // Not all programs reliably export them, and sometimes you need to override them.
  // So we have this system where if the developer uses icon directly and sets a viewbox that is first used.
  // If the SVG when generated with SVGR had a native viewbox we use that, and finally if none is set we make the viewbox width and height.
  const finalViewbox = rest?.viewBox || nativeViewbox || '0 0 24 24'

  return (
    <SvgIcon
      viewBox={finalViewbox}
      classes={{
        root: cx(!isMuiColor && classes[color]),
      }}
      color={isMuiColor ? color : 'inherit'}
      {...rest}
    >
      {children}
    </SvgIcon>
  )
}

export default Icon
